/**
 * Email Triggers plugin — settings in localStorage; sending uses POST /projects/:id/phase-trigger-email
 * when the API registers routes in `backend/scripts/phaseEmailRoutes.cjs`.
 */

import {
  EMAIL_TRIGGER_ATTACHMENT_SOURCE_IDS,
  defaultAttachmentSourcesForTrigger,
} from "./emailTriggerAttachments";
import { SUBPHASE_EMAIL_TRIGGER_DEFINITIONS } from "./subphaseEmailTriggers";
import { PERMIT_RIDER_EMAIL_TRIGGER_DEFINITIONS } from "./permitRiderEmailTriggers";
import { PERMIT_EXPIRING_EMAIL_TRIGGER_DEFINITIONS } from "./permitExpiringEmailTriggers";
import { PERMIT_FOLLOW_UP_EMAIL_TRIGGER_DEFINITIONS } from "./permitFollowUpEmailTriggers";
import { INSPECTOR_REPORT_SUMMARY_EMAIL_TRIGGER_DEFINITIONS } from "./inspectorReportSummaryEmailTriggers";
import { COMMERCIAL_AG_GRID_WEEKLY_EMAIL_TRIGGER_DEFINITIONS } from "./commercialAgGridWeeklyEmailTriggers";

export const EMAIL_TRIGGERS_STORAGE_KEY = "pb.plugin.emailTriggers.v1";

let emailTriggersCache = null;

function settingsHasConfiguredTriggers(settings) {
  for (const def of EMAIL_TRIGGER_DEFINITIONS) {
    const tr = settings?.triggers?.[def.id];
    if (!tr) continue;
    if (Array.isArray(tr.recipientRoleTypeIds) && tr.recipientRoleTypeIds.length > 0) return true;
    if (Array.isArray(tr.recipientEmails) && tr.recipientEmails.length > 0) return true;
    if (String(tr.subject || "").trim()) return true;
    if (String(tr.body || "").trim()) return true;
  }
  return false;
}

export function hydrateEmailTriggersFromServer(raw) {
  let normalized = normalizeSettings(raw);
  if (!settingsHasConfiguredTriggers(normalized)) {
    try {
      const localRaw = localStorage.getItem(EMAIL_TRIGGERS_STORAGE_KEY);
      if (localRaw) {
        const local = normalizeSettings(JSON.parse(localRaw));
        if (settingsHasConfiguredTriggers(local)) normalized = local;
      }
    } catch {
      /* ignore */
    }
  }
  emailTriggersCache = normalized;
  try {
    localStorage.setItem(EMAIL_TRIGGERS_STORAGE_KEY, JSON.stringify(normalized));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent("pb-email-triggers-settings-changed"));
}

export function clearEmailTriggersHydration() {
  emailTriggersCache = null;
}

/** Fixed list of milestone triggers (ids stable for stored settings). */
export const EMAIL_TRIGGER_DEFINITIONS = [
  { id: "new_work_order", label: "New Work Order", group: "Milestones" },
  { id: "new_customer", label: "New Customer", group: "Milestones" },
  { id: "sales_complete", label: "Sales Complete", group: "Milestones" },
  { id: "finance_complete", label: "Finance Complete", group: "Milestones" },
  {
    id: "finance_complete_fiber_networking_only",
    label: "Finance Complete (Fiber and Networking Only)",
    group: "Milestones",
  },
  { id: "finance_complete_network_only", label: "Finance Complete (Network Only)", group: "Milestones" },
  { id: "drafting_complete", label: "Drafting Complete", group: "Milestones" },
  { id: "permitting_complete", label: "Permitting Complete", group: "Milestones" },
  { id: "construction_complete", label: "Construction Complete", group: "Milestones" },
  { id: "fiber_complete", label: "Fiber Complete", group: "Milestones" },
  { id: "networking_complete", label: "Networking Complete", group: "Milestones" },
  { id: "accounting_complete", label: "Accounting Complete", group: "Milestones" },
  { id: "project_closed", label: "Project Closed", group: "Milestones" },
  ...PERMIT_RIDER_EMAIL_TRIGGER_DEFINITIONS,
  ...PERMIT_EXPIRING_EMAIL_TRIGGER_DEFINITIONS,
  ...PERMIT_FOLLOW_UP_EMAIL_TRIGGER_DEFINITIONS,
  ...INSPECTOR_REPORT_SUMMARY_EMAIL_TRIGGER_DEFINITIONS,
  ...COMMERCIAL_AG_GRID_WEEKLY_EMAIL_TRIGGER_DEFINITIONS,
  ...SUBPHASE_EMAIL_TRIGGER_DEFINITIONS,
];

function emptyTriggerConfig(triggerId) {
  return {
    recipientRoleTypeIds: [],
    recipientEmails: [],
    subject: "",
    body: "",
    attachmentSources: defaultAttachmentSourcesForTrigger(triggerId || ""),
  };
}

function normalizeRecipientEmails(raw) {
  if (Array.isArray(raw)) {
    return [
      ...new Set(
        raw
          .map((e) => String(e || "").trim().toLowerCase())
          .filter((e) => e && e.includes("@"))
      ),
    ].sort();
  }
  if (typeof raw === "string") {
    return [
      ...new Set(
        raw
          .split(/[\s,;]+/)
          .map((e) => e.trim().toLowerCase())
          .filter((e) => e && e.includes("@"))
      ),
    ].sort();
  }
  return [];
}

function defaultSettings() {
  const triggers = {};
  for (const t of EMAIL_TRIGGER_DEFINITIONS) {
    triggers[t.id] = emptyTriggerConfig(t.id);
  }
  return {
    triggers,
  };
}

function normalizeSettings(raw) {
  const base = defaultSettings();
  if (!raw || typeof raw !== "object") return base;
  const triggers = { ...base.triggers };
  if (raw.triggers && typeof raw.triggers === "object") {
    for (const def of EMAIL_TRIGGER_DEFINITIONS) {
      const tr = raw.triggers[def.id];
      if (!tr || typeof tr !== "object") continue;
      const recipientRoleTypeIds = Array.isArray(tr.recipientRoleTypeIds)
        ? [...new Set(tr.recipientRoleTypeIds.map((e) => String(e || "").trim()).filter(Boolean))]
        : [];
      const attachmentSources = Array.isArray(tr.attachmentSources)
        ? [
            ...new Set(
              tr.attachmentSources
                .map((id) => String(id || "").trim())
                .filter((id) => EMAIL_TRIGGER_ATTACHMENT_SOURCE_IDS.has(id))
            ),
          ]
        : defaultAttachmentSourcesForTrigger(def.id);
      triggers[def.id] = {
        recipientRoleTypeIds,
        recipientEmails: normalizeRecipientEmails(tr.recipientEmails),
        subject: typeof tr.subject === "string" ? tr.subject : "",
        body: typeof tr.body === "string" ? tr.body : "",
        attachmentSources,
      };
    }
    const legacy = raw.triggers.engineering_complete;
    if (legacy && typeof legacy === "object") {
      const d = triggers.drafting_complete;
      const legacyHas =
        (Array.isArray(legacy.recipientRoleTypeIds) && legacy.recipientRoleTypeIds.length > 0) ||
        (typeof legacy.subject === "string" && legacy.subject.trim() !== "") ||
        (typeof legacy.body === "string" && legacy.body.trim() !== "");
      const draftEmpty =
        (!Array.isArray(d.recipientRoleTypeIds) || d.recipientRoleTypeIds.length === 0) &&
        String(d.subject || "").trim() === "" &&
        String(d.body || "").trim() === "";
      if (legacyHas && draftEmpty) {
        triggers.drafting_complete = {
          recipientRoleTypeIds: Array.isArray(legacy.recipientRoleTypeIds)
            ? [...new Set(legacy.recipientRoleTypeIds.map((e) => String(e || "").trim()).filter(Boolean))]
            : [],
          recipientEmails: [],
          subject: typeof legacy.subject === "string" ? legacy.subject : "",
          body: typeof legacy.body === "string" ? legacy.body : "",
        };
      }
    }
  }
  return { triggers };
}

export function loadEmailTriggersSettings() {
  if (emailTriggersCache) return emailTriggersCache;
  try {
    const raw = localStorage.getItem(EMAIL_TRIGGERS_STORAGE_KEY);
    if (!raw) return defaultSettings();
    return normalizeSettings(JSON.parse(raw));
  } catch {
    return defaultSettings();
  }
}

export async function saveEmailTriggersSettings(settings) {
  const normalized = normalizeSettings(settings);
  emailTriggersCache = normalized;
  try {
    localStorage.setItem(EMAIL_TRIGGERS_STORAGE_KEY, JSON.stringify(normalized));
  } catch {
    /* ignore */
  }
  try {
    const { putAdminAppConfig } = await import("./appConfigApi.js");
    const cfg = await putAdminAppConfig({ emailTriggers: normalized });
    if (cfg?.emailTriggers) hydrateEmailTriggersFromServer(cfg.emailTriggers);
  } catch (err) {
    console.error("Could not sync email triggers to server:", err?.response?.data?.error || err.message);
  }
  window.dispatchEvent(new CustomEvent("pb-email-triggers-settings-changed"));
}
